<script setup lang="ts">
import Mousetrap from "mousetrap";
import { useRouter } from "vue-router";
import { useSelector } from "@xstate/vue";
import { ConfigProvider } from "reka-ui";
import { inject, onMounted, ref, watch } from "vue";
import { ToastProvider, ToastRoot, ToastViewport, ToastAction, ToastDescription, ToastTitle } from "reka-ui";

import AuthError from "@/pages/Auth.vue";

import Loading from "@/components/Loading.vue";
import RsMessage from "@/components/Message.vue";

import { authMachine } from "@/machines/auth-machine";

import { useNetworkStatus } from "@/hooks/use-network-status";

const CSP_NONCE = inject<string>("NONCE");

const $router = useRouter();

const isOnline = useNetworkStatus();

const authCompletedOnce = ref(false);

const isErrored = useSelector(authMachine, (state) => state.matches("error"));
const isAuthenticated = useSelector(authMachine, (state) => state.matches("authenticated"));

watch(
  isAuthenticated,
  () => {
    if (authCompletedOnce.value || !isAuthenticated.value) return;
    authCompletedOnce.value = true;
  },
  { immediate: true },
);

onMounted(() => {
  Mousetrap.bind("l v", () => {
    $router.push({ name: "logs" });
  });
});
</script>

<template>
  <ConfigProvider v-if="authCompletedOnce" :nonce="CSP_NONCE">
    <div id="popper-portal" />
    <div id="modals-portal" />
    <RsMessage />
    <router-view />
    <ToastProvider :duration="Infinity">
      <ToastRoot
        type="background"
        class="tw-overflow-hidden tw-border tw-border-orange-500 tw-bg-white"
        :open="isErrored"
      >
        <div
          :style="{ gridTemplateAreas: `'title action' 'description action'` }"
          class="tw-grid tw-grid-cols-[minmax(0,1fr)_max-content] tw-bg-orange-200/30 tw-p-4 tw-shadow"
        >
          <ToastTitle class="tw-font-semibold tw-tracking-wide" :style="{ gridArea: 'title' }">
            Authentication Error
          </ToastTitle>
          <ToastDescription :style="{ gridArea: 'description' }" class="tw-text-xs">
            Error refreshing account session. Use the button to manually retry the process and prevent data loss.
          </ToastDescription>
          <div :style="{ gridArea: 'action' }">
            <ToastAction
              alt-text="Reload the page"
              class="tw-btn-md-gray tw-rounded"
              @click="() => authMachine.send({ type: 'EXTERNAL_LOGIN' })"
            >
              Log In
            </ToastAction>
          </div>
        </div>
      </ToastRoot>
      <ToastRoot :open="!isOnline" type="background" class="tw-overflow-hidden tw-border tw-border-red-800 tw-bg-white">
        <div class="tw-grid tw-bg-red-300/20 tw-p-4 tw-shadow">
          <ToastTitle class="tw-font-semibold tw-tracking-wide">No Internet Connection</ToastTitle>
          <ToastDescription class="tw-text-xs">
            Your internet connection has been lost. Some features may be unavailable until the connection is restored.
          </ToastDescription>
        </div>
      </ToastRoot>
      <ToastViewport
        class="tw-fixed tw-bottom-3 tw-right-3 tw-z-[100000] tw-w-full tw-max-w-md tw-space-y-2 tw-rounded-md [&>*]:tw-overflow-hidden [&>*]:tw-rounded-[inherit]"
      />
    </ToastProvider>
  </ConfigProvider>
  <AuthError v-else-if="isErrored" />
  <Loading v-else id="loading" message="Authenticating..." />
</template>
